import { T, useGT } from 'gt-react';
import { Warning as WarningIcon } from '@phosphor-icons/react';
import {
  DiscountElementTargetType,
  DiscountElementType,
} from '@erp/shared-types';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Autocomplete } from '@/components/common/inputs/autocomplete';
import { pickerLabels } from '@/components/common/inputs/pickerLabels';
import { FormattedInput } from '@/components/ui/formatted-input';
import { useProductCatalogClassifications } from '@/api/inventory-management/useProductCatalog';
import { VariantPicker } from './VariantPicker';
import { TARGET_TYPE_LABELS } from './targetTypeLabels';

/**
 * Shared editable element-row machinery for the Discounts v2 surfaces: the
 * create-group dialog's tabular editor and the group review page's inline
 * element editing both render the same cells and share the same draft shape,
 * validation, and wire conversion.
 */

export interface ElementDraft {
  key: number;
  discountType: DiscountElementType;
  discountValue: string;
  targetType: DiscountElementTargetType;
  /** Variant id for PRODUCT_VARIANT_ID targets; segment text otherwise. */
  targetValue: string;
  /** Display label for a chosen variant. */
  targetLabel: string;
  /** Product name for a chosen variant (display only). */
  targetDescription: string;
  maxQuantity: string;
  isSingleUse: boolean;
  /** Per-element effective window (review-page editing; dialog leaves empty). */
  effectiveStart: string;
  effectiveEnd: string;
}

export const TYPE_LABELS: Record<DiscountElementType, string> = {
  [DiscountElementType.FIXED_PRICE]: 'Fixed price',
  [DiscountElementType.PERCENT_BPS_OFF]: 'Percentage off',
  [DiscountElementType.AMOUNT_OFF]: 'Amount off',
};

/**
 * Dec 31 of the current year — the default expiry for discounts entered
 * without one. Every element must carry an end date (contract-enforced).
 */
export function defaultEffectiveEnd(): string {
  return `${new Date().getFullYear()}-12-31`;
}

let nextKey = 1;
export function emptyElement(): ElementDraft {
  return {
    key: nextKey++,
    discountType: DiscountElementType.FIXED_PRICE,
    discountValue: '',
    targetType: DiscountElementTargetType.PRODUCT_VARIANT_ID,
    targetValue: '',
    targetLabel: '',
    targetDescription: '',
    maxQuantity: '',
    isSingleUse: false,
    effectiveStart: '',
    effectiveEnd: '',
  };
}

/**
 * Whether a placeholder element (created by ingestion for a row that named
 * the product but no price — see `review.unresolvedLines[].elementId`) still
 * has no price entered. Provenance, not the value, decides "placeholder": a
 * deliberate $0 fixed price is a real discount and carries no warning.
 */
export function isMissingPrice(
  isPlaceholder: boolean,
  discountValue: string | number,
): boolean {
  if (!isPlaceholder) return false;
  const value = Number(String(discountValue).trim());
  return !(value > 0);
}

/** Small hazard sign shown next to a value that the request did not carry. */
export function MissingPriceWarning() {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex shrink-0">
          <WarningIcon
            weight="fill"
            className="size-3.5 shrink-0 text-amber-500"
          />
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <T context="tooltip on a discount element whose value is 0 because the ingested request had no price">
          No price on the request — enter one
        </T>
      </TooltipContent>
    </Tooltip>
  );
}

function isPercentOutOfRange(el: ElementDraft, value: number): boolean {
  return el.discountType === DiscountElementType.PERCENT_BPS_OFF && value > 100;
}

function valueIssue(el: ElementDraft): string | null {
  // Guard blank input first: Number('') === 0, which would read as a valid $0.
  if (!el.discountValue.trim()) return 'Value required';
  const value = Number(el.discountValue);
  // A $0 fixed price is a real discount (free item); 0% or $0 off is a no-op.
  const zeroAllowed = el.discountType === DiscountElementType.FIXED_PRICE;
  if (!Number.isFinite(value) || (zeroAllowed ? value < 0 : value <= 0)) {
    return 'Value required';
  }
  if (isPercentOutOfRange(el, value)) {
    return 'Percentage must be between 0 and 100';
  }
  return null;
}

function targetIssue(el: ElementDraft): string | null {
  if (
    el.targetType !== DiscountElementTargetType.ALL_PRODUCTS &&
    !el.targetValue.trim()
  ) {
    return 'Target required';
  }
  return null;
}

export function elementIssue(el: ElementDraft): string | null {
  return valueIssue(el) ?? targetIssue(el);
}

/** Percent entered as a percentage becomes basis points; money stays as is. */
function wireDiscountValue(type: DiscountElementType, value: string): number {
  if (type === DiscountElementType.PERCENT_BPS_OFF) {
    return Math.min(10000, Math.max(1, Math.round(Number(value) * 100)));
  }
  return Number(value);
}

/** ALL_PRODUCTS targets nothing specific; everything else targets the value. */
function wireTargetValue(
  type: DiscountElementTargetType,
  value: string,
): string | null {
  return type === DiscountElementTargetType.ALL_PRODUCTS ? null : value.trim();
}

/**
 * Convert a draft row into the wire element shape (percent entered as a
 * percentage becomes basis points). Callers layer group-level dates on top
 * when the row carries none; a row with no end date expires at year end.
 */
export function draftToElementInput(el: ElementDraft) {
  return {
    discountType: el.discountType,
    discountValue: wireDiscountValue(el.discountType, el.discountValue),
    targetType: el.targetType,
    targetValue: wireTargetValue(el.targetType, el.targetValue),
    effectiveStart: el.effectiveStart || null,
    effectiveEnd: el.effectiveEnd || defaultEffectiveEnd(),
    isSingleUse: el.isSingleUse,
    maxQuantity: el.maxQuantity ? Number(el.maxQuantity) : null,
  };
}

/**
 * PRODVIEW segment suggestions from the same classification source as the
 * Product Catalog filters.
 */
export function useSegmentOptions(): Partial<
  Record<DiscountElementTargetType, string[]>
> {
  const { data: classificationsData } = useProductCatalogClassifications();
  const classifications = classificationsData?.classifications ?? [];
  return {
    [DiscountElementTargetType.PRODUCT_CATEGORY]: [
      ...new Set(classifications.map((c) => c.family)),
    ].sort(),
    [DiscountElementTargetType.PRODUCT_LINE]: [
      ...new Set(classifications.map((c) => c.group)),
    ].sort(),
    [DiscountElementTargetType.PRODUCT_FAMILY]: [
      ...new Set(classifications.map((c) => c.model)),
    ].sort(),
  };
}

/** Autocomplete over real PRODVIEW segment values (catalog-sourced). */
export function SegmentTargetPicker({
  options,
  value,
  onChange,
  searchPlaceholder,
}: {
  options: string[];
  value: string;
  onChange: (value: string) => void;
  searchPlaceholder: string;
}) {
  return (
    <Autocomplete
      items={options}
      getValue={(option) => option}
      getLabel={(option) => option}
      value={value || null}
      onValueChange={(v) => onChange(v ?? '')}
      deselectable
      allowClear
      labels={pickerLabels({ placeholder: '', search: searchPlaceholder })}
      className="w-full"
    />
  );
}

/**
 * The target-value cell: the editor swaps between a variant picker, a fixed
 * "every product" label, a segment autocomplete, and a free-text input based
 * on the row's target type.
 */
/** Segment autocomplete when catalog values exist, free-text input otherwise. */
function SegmentOrTextTarget({
  row,
  onPatch,
  options,
}: {
  row: ElementDraft;
  onPatch: (patch: Partial<ElementDraft>) => void;
  options: string[] | undefined;
}) {
  const t = useGT();
  if (options) {
    return (
      <SegmentTargetPicker
        options={options}
        value={row.targetValue}
        onChange={(value) => onPatch({ targetValue: value })}
        searchPlaceholder={t('Search values…')}
      />
    );
  }
  return (
    <Input
      className="h-8"
      placeholder={t('e.g. 1710')}
      value={row.targetValue}
      onChange={(e) => onPatch({ targetValue: e.target.value })}
    />
  );
}

function TargetValueCell({
  row,
  onPatch,
  segmentOptions,
}: {
  row: ElementDraft;
  onPatch: (patch: Partial<ElementDraft>) => void;
  segmentOptions: Partial<Record<DiscountElementTargetType, string[]>>;
}) {
  return (
    <td className="w-full max-w-0 min-w-56 p-2">
      {row.targetType === DiscountElementTargetType.PRODUCT_VARIANT_ID ? (
        <VariantPicker
          value={row.targetValue}
          label={row.targetLabel}
          description={row.targetDescription}
          onSelect={(variantId, label, description) =>
            onPatch({
              targetValue: variantId,
              targetLabel: label,
              targetDescription: description ?? '',
            })
          }
        />
      ) : row.targetType === DiscountElementTargetType.ALL_PRODUCTS ? (
        <span className="block pt-1.5 text-xs text-muted-foreground">
          <T>Every product</T>
        </span>
      ) : (
        <SegmentOrTextTarget
          row={row}
          onPatch={onPatch}
          options={segmentOptions[row.targetType]}
        />
      )}
    </td>
  );
}

/**
 * The editable cells for one element row (shared by the blank add-row and the
 * committed rows) — target type/value, discount type/value, max qty,
 * single-use.
 */
export function ElementRowCells({
  row,
  onPatch,
  segmentOptions,
  isPlaceholder = false,
}: {
  row: ElementDraft;
  onPatch: (patch: Partial<ElementDraft>) => void;
  segmentOptions: Partial<Record<DiscountElementTargetType, string[]>>;
  /**
   * The row is an ingestion placeholder (linked from a review line): flag it
   * until a price is entered. Committed rows only.
   */
  isPlaceholder?: boolean;
}) {
  const t = useGT();
  const missingPrice = isMissingPrice(isPlaceholder, row.discountValue);
  return (
    <>
      <td className="w-36 min-w-36 p-2">
        <Select
          value={row.targetType}
          onValueChange={(v) =>
            onPatch({
              targetType: v as DiscountElementTargetType,
              targetValue: '',
              targetLabel: '',
              targetDescription: '',
            })
          }
        >
          <SelectTrigger className="h-8 w-full text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(TARGET_TYPE_LABELS)
              .filter(
                ([value]) => value !== DiscountElementTargetType.PRODUCT_GROUP,
              )
              .map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </td>
      <TargetValueCell
        row={row}
        onPatch={onPatch}
        segmentOptions={segmentOptions}
      />
      <td className="w-36 min-w-36 p-2">
        <Select
          value={row.discountType}
          onValueChange={(v) =>
            onPatch({ discountType: v as DiscountElementType })
          }
        >
          <SelectTrigger className="h-8 w-full text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(TYPE_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="w-36 min-w-36 p-2">
        <div className="flex items-center gap-1">
          {missingPrice && <MissingPriceWarning />}
          <FormattedInput
            formatType={
              row.discountType === DiscountElementType.PERCENT_BPS_OFF
                ? 'percentage'
                : 'currency'
            }
            allowNegative={false}
            placeholder={
              row.discountType === DiscountElementType.PERCENT_BPS_OFF
                ? '0'
                : '0.00'
            }
            value={row.discountValue}
            onChange={(value) => onPatch({ discountValue: value })}
            className="h-8"
          />
        </div>
      </td>
      <td className="w-32 min-w-32 p-2">
        <Input
          className="h-8"
          type="number"
          min="0.0001"
          step="any"
          inputMode="decimal"
          placeholder={t('Unlimited')}
          value={row.maxQuantity}
          onChange={(e) => onPatch({ maxQuantity: e.target.value })}
        />
      </td>
      <td className="p-2 text-center">
        <Checkbox
          checked={row.isSingleUse}
          onCheckedChange={(v) => onPatch({ isSingleUse: v === true })}
        />
      </td>
    </>
  );
}
